import {
  ASSET_VIEW_LABEL,
  assetViewForModelKind,
  usePermittedModelKinds,
} from 'features/assetDatabase/assetViews'
import type { CommandPaletteTarget } from 'features/commandPalette/utils/commandPaletteTargets'
import { SelectedTab } from 'features/navbar/types'
import {
  hasModelKind,
  MODEL_KIND_ICON,
  MODEL_KIND_LABEL,
} from 'features/threeDModels/modelKinds'
import { useThreeDModelRows } from 'features/threeDModels/useThreeDModelRows'
import { useMemo } from 'react'
import { createWalkthroughUrl } from 'routes/walkthrough/-searchParams'
import type { ModelKind } from 'types'

import { useIsNavItemPermitted } from './useIsNavItemPermitted'

const NO_KINDS: ReadonlyArray<ModelKind> = []

export function useCommandPaletteModelTargets({
  siteId,
  orgId,
  enabled,
}: {
  siteId: string | undefined
  orgId: string | undefined
  enabled: boolean
}): CommandPaletteTarget[] {
  const isNavItemPermitted = useIsNavItemPermitted({ siteId, orgId })
  const permittedKinds = usePermittedModelKinds() ?? NO_KINDS
  const canOpenModels =
    isNavItemPermitted(SelectedTab.Assets) && permittedKinds.length > 0
  const { rows } = useThreeDModelRows(
    enabled && canOpenModels ? siteId : undefined
  )

  return useMemo(() => {
    if (!siteId || !canOpenModels) return []
    return rows.flatMap((row) =>
      permittedKinds
        .filter((kind) => hasModelKind(row, kind))
        .map((kind) => ({
          id: `model:${row.key}:${kind}`,
          kind: 'model' as const,
          label: `${row.title} · ${MODEL_KIND_LABEL[kind]}`,
          kindLabel: ASSET_VIEW_LABEL[assetViewForModelKind(kind)],
          icon: MODEL_KIND_ICON[kind],
          href: createWalkthroughUrl({
            orgId,
            siteId,
            asset: row.key,
            view: kind,
          }),
        }))
    )
  }, [canOpenModels, orgId, permittedKinds, rows, siteId])
}
